import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, Zap, Landmark, Shield, Lock, Star, ArrowRight, Home, BadgeCheck } from 'lucide-react';
import { Button } from '../ui/components';
import { setUser } from '../store/loanApplicationSlice';

const AccountCreationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const savedData = location.state?.savedData || {};
  const recommendedProduct = location.state?.recommendedProduct || null;
  
  // Determine if this is a purchase or refinance based on saved data
  const loanPurpose = savedData?.loanPurpose || savedData?.purpose || 'purchase';
  const isPurchase = loanPurpose === 'purchase' || loanPurpose === 'Purchase';
  const isRefinance = loanPurpose === 'refinance' || loanPurpose === 'Refinance' || loanPurpose === 'cashout';
  
  const [authMethod, setAuthMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    agreeToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const federatedProviders = [
    { id: 'google', name: 'Google', color: '#333', bgColor: '#FFF', borderColor: '#E0E0E0' },
    { id: 'facebook', name: 'Facebook', color: '#FFF', bgColor: '#1877F2', borderColor: '#1877F2' },
  ];

  const handleFederatedAuth = (provider) => {
    setIsLoading(true);
    setAuthMethod(provider.id);
    
    setTimeout(() => {
      dispatch(setUser({
        email: `user@${provider.id}.com`,
        firstName: 'Demo',
        lastName: 'User',
        authMethod: provider.id,
      }));
      navigate('/apply', { state: { step: 1, savedData, fromAccountCreation: true } });
    }, 1500);
  };

  const handleEmailSignup = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'Please agree to continue';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);
    setAuthMethod('email');
    
    setTimeout(() => {
      dispatch(setUser({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        authMethod: 'email',
      }));
      navigate('/apply', { state: { step: 1, savedData, fromAccountCreation: true } });
    }, 1500);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const testimonials = [
    { name: 'Sarah M.', location: 'Austin, TX', text: 'The preparation made everything so much easier with my lender.', rating: 5 },
    { name: 'Michael R.', location: 'Denver, CO', text: 'Comparing offers helped me find a rate 0.25% lower.', rating: 5 },
    { name: 'Jennifer L.', location: 'Seattle, WA', text: 'Finally understood my mortgage options clearly.', rating: 5 },
  ];

  const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid var(--ds-border-default)', borderRadius: 'var(--ds-border-radius-medium)', fontSize: '0.95rem', outline: 'none' };
  const errorInputStyle = { ...inputStyle, borderColor: '#dc2626' };
  const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--ds-text-primary)', marginBottom: 4 };
  const helperStyle = { fontSize: '0.75rem', marginTop: 4 };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FAFAFA' }}>
        <div style={{ padding: 48, textAlign: 'center', borderRadius: 'var(--ds-border-radius-large)', maxWidth: 400, backgroundColor: 'var(--ds-bg-primary)', border: '1px solid var(--ds-border-default)' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: 'var(--ds-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Home size={40} style={{ color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 8 }}>Creating Your Account</h2>
          <p style={{ color: 'var(--ds-text-secondary)', marginBottom: 24 }}>Setting up your personalized dashboard...</p>
          <div style={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ width: '60%', height: '100%', backgroundColor: 'var(--ds-primary)', animation: 'pulse 1.5s infinite' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Left Side - Value Proposition */}
        <div className="lk-desktop-only" style={{ flex: 1, backgroundColor: 'var(--ds-primary)', color: 'white', padding: '48px 48px 48px 96px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'relative', zIndex: 1, maxWidth: 480, marginRight: 32 }}>
            <div style={{ marginBottom: 48 }}>
              <img src="/assets/loanknow-logo.svg" alt="LoanKnow" style={{ height: 32, filter: 'brightness(0) invert(1)' }} />
            </div>
            <h1 style={{ fontSize: '2.25rem', fontWeight: 700, marginBottom: 16, lineHeight: 1.2 }}>
              {isRefinance ? "Unlock Better Rates on Your Current Home" : "Your Dream Home is Closer Than You Think"}
            </h1>
            <p style={{ fontSize: '1.1rem', marginBottom: 40, opacity: 0.85, lineHeight: 1.6 }}>
              {isRefinance ? "See if refinancing could lower your monthly payment. Get organized and compare lender offers in one place." : "Get organized, compare lender offers, and be fully prepared before you start house hunting."}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {[
                { icon: <Zap size={24} />, title: 'Be Closing-Ready', desc: 'Organized applications help lenders process faster — no back-and-forth delays' },
                { icon: <Landmark size={24} />, title: 'Compare Multiple Lenders', desc: 'Receive competitive offers from our network of trusted lenders' },
                { icon: <Shield size={24} />, title: 'Bank-Level Security', desc: 'Your data is encrypted and never shared without permission' },
              ].map((b) => (
                <div key={b.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{b.icon}</div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{b.title}</div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Sign Up Form */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 48px' }}>
          <div style={{ width: '100%', maxWidth: 480 }}>
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 8, color: 'var(--ds-text-primary)' }}>Create Your Free Account</h2>
              <p style={{ color: 'var(--ds-text-secondary)', fontSize: '1.05rem' }}>
                {isRefinance ? "Start exploring your refinance options. It only takes 2 minutes." : "Start your journey to homeownership today. It only takes 2 minutes."}
              </p>
            </div>

            {recommendedProduct && (
              <div style={{ padding: '12px 16px', marginBottom: 24, borderRadius: 'var(--ds-border-radius-medium)', backgroundColor: 'rgba(5,148,79,0.08)', border: '1px solid rgba(5,148,79,0.2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <BadgeCheck size={20} style={{ color: '#16a34a', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem' }}><strong>Great news!</strong> Based on your profile, you qualify for <strong>{recommendedProduct}</strong></span>
              </div>
            )}

            {/* Social Sign Up */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {federatedProviders.map((p) => (
                <button key={p.id} onClick={() => handleFederatedAuth(p)} style={{ width: '100%', padding: '14px 16px', border: `1px solid ${p.borderColor}`, borderRadius: 'var(--ds-border-radius-medium)', backgroundColor: p.bgColor, color: p.color, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}>
                  Continue with {p.name}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0' }}>
              <hr style={{ flex: 1, border: 'none', borderTop: '1px solid var(--ds-border-default)' }} />
              <span style={{ padding: '0 16px', fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>or continue with email</span>
              <hr style={{ flex: 1, border: 'none', borderTop: '1px solid var(--ds-border-default)' }} />
            </div>

            {/* Email Form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First Name</label>
                  <input style={errors.firstName ? errorInputStyle : inputStyle} value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} />
                  {errors.firstName && <div style={{ ...helperStyle, color: '#dc2626' }}>{errors.firstName}</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last Name</label>
                  <input style={errors.lastName ? errorInputStyle : inputStyle} value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} />
                  {errors.lastName && <div style={{ ...helperStyle, color: '#dc2626' }}>{errors.lastName}</div>}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Email Address</label>
                <input type="email" style={errors.email ? errorInputStyle : inputStyle} value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} />
                {errors.email && <div style={{ ...helperStyle, color: '#dc2626' }}>{errors.email}</div>}
              </div>

              <div>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} style={errors.password ? errorInputStyle : inputStyle} value={formData.password} onChange={(e) => handleInputChange('password', e.target.value)} />
                  <button onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-text-secondary)' }}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div style={{ ...helperStyle, color: errors.password ? '#dc2626' : 'var(--ds-text-tertiary)' }}>{errors.password || 'At least 8 characters'}</div>
              </div>

              <div>
                <label style={labelStyle}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showConfirmPassword ? 'text' : 'password'} style={errors.confirmPassword ? errorInputStyle : inputStyle} value={formData.confirmPassword} onChange={(e) => handleInputChange('confirmPassword', e.target.value)} />
                  <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-text-secondary)' }}>
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && <div style={{ ...helperStyle, color: '#dc2626' }}>{errors.confirmPassword}</div>}
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.agreeToTerms} onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)} style={{ marginTop: 3 }} />
                <span style={{ fontSize: '0.875rem', color: errors.agreeToTerms ? '#dc2626' : 'var(--ds-text-secondary)' }}>
                  I agree to the <a href="#" style={{ color: 'var(--ds-primary)' }}>Terms of Service</a> and <a href="#" style={{ color: 'var(--ds-primary)' }}>Privacy Policy</a>
                </span>
              </label>

              <Button variant="primary" size="lg" onClick={handleEmailSignup} style={{ width: '100%' }}>
                Create Free Account <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </Button>
            </div>

            {/* Security Badge */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 24, color: 'var(--ds-text-secondary)' }}>
              <Lock size={14} />
              <span style={{ fontSize: '0.75rem' }}>256-bit SSL encryption &bull; Your data is secure</span>
            </div>

            {/* Sign In Link */}
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--ds-text-secondary)' }}>
                Already have an account? <a href="/login" style={{ color: 'var(--ds-primary)', fontWeight: 600 }}>Sign In</a>
              </span>
            </div>

            {/* Testimonial */}
            <div style={{ marginTop: 32, padding: 24, borderRadius: 'var(--ds-border-radius-large)', backgroundColor: 'var(--ds-bg-secondary)' }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="#F6A623" style={{ color: '#F6A623' }} />)}
              </div>
              <p style={{ fontSize: '0.875rem', fontStyle: 'italic', color: 'var(--ds-text-secondary)', marginBottom: 16 }}>"{testimonials[0].text}"</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--ds-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.875rem' }}>
                  {testimonials[0].name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{testimonials[0].name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--ds-text-tertiary)' }}>{testimonials[0].location}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountCreationPage;
